import { resourceKeySchema } from './common';

/**
 * A first guess at a stable key, from the name somebody actually typed.
 *
 * One function rather than the three near-copies this replaced (the map's
 * `suggestKey`, the platform's `slugify`, and the two forms that had neither
 * and so asked people to invent a key by hand). They differed only in whether
 * they validated the result, which is not a difference worth three
 * implementations of the same accent folding.
 *
 * It lives in the contracts package rather than in the browser because the
 * GeoJSON import needs it on the server: a file of four hundred features
 * arrives with names and no keys, and deriving them there by a second
 * implementation would be the fourth copy this function exists to prevent.
 *
 * Accents are folded rather than stripped, so "Pasilan varikkö" suggests
 * `pasilan-varikko` and not `pasilan-varikk`, and "Pöytäkirja" becomes
 * `poytakirja` rather than `p-yt-kirja`. This is a Finnish and Swedish
 * product; a key generator that mangles the letters half the names contain is
 * one nobody would leave switched on.
 *
 * Returns an empty string when nothing usable is left -- shown as an empty
 * field rather than as a suggestion the server would reject.
 */
export function suggestResourceKey(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    // Trimmed after the slice, not before: cutting at 64 characters can land on
    // a hyphen, and a trailing hyphen fails the schema.
    .slice(0, 64)
    .replace(/^-+|-+$/g, '');

  return resourceKeySchema.safeParse(slug).success ? slug : '';
}
